import React from 'react';
import useStyles from './styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';
import Grid2 from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Image from 'next/image';
import { Link, Copyright, darkTheme } from '@elevator/ui';
import { FOOTER_ROUTES, SOCIAL_MEDIA } from 'config/Nav';
import { Box, Divider, Paper } from '@mui/material';

/**
 * Footer Component
 *
 * Site footer component displaying navigation links, social media icons, logo,
 * and copyright notice. Always renders in dark theme. Responsive layout adapts
 * to mobile and desktop breakpoints.
 *
 * Features:
 * - Navigation links (Staff, Advertise, FAQ, Contact)
 * - Social media icons with links
 * - Site logo
 * - Copyright notice with icon
 * - Dark theme styling
 * - Responsive layout (mobile/desktop)
 *
 * Used in:
 * - Site layout (appears on all pages)
 *
 * @returns Footer component with navigation, social links, and copyright
 */
const Footer = () => {
  const { classes } = useStyles();

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const renderSocialMedia = () => {
    return (
      <Grid2
        container
        spacing={4}
        className={classes.socialMediaContainer}
        sx={{
          alignItems: 'center',
          justifyContent: mobile ? 'center' : 'flex-end',
        }}
      >
        {Object.values(SOCIAL_MEDIA).map((routeConfig, index) => (
          <Grid2 key={index}>
            <a
              href={routeConfig.route}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={
                routeConfig.type === 'x'
                  ? 'Visit us on X'
                  : `Visit our ${routeConfig.type}`
              }
            >
              <routeConfig.icon className={classes.socialMediaIcon} />
            </a>
          </Grid2>
        ))}
      </Grid2>
    );
  };

  const renderTopLinks = () => {
    // Filter and order links: Staff, Advertise, Help, FAQ, Contact
    const linkOrder = ['Staff', 'Advertise', 'Help', 'FAQ', 'Contact'];
    const topLinks = linkOrder
      .map((linkText) => FOOTER_ROUTES.find((route) => route.navLinkText === linkText))
      .filter((route) => route !== undefined);

    return (
      <Stack direction="row" alignItems="center" className={classes.topLinksContainer}>
        {topLinks.map(({ navLinkText, to, external, target }, index) => (
          <Link
            key={index}
            href={to}
            external={external}
            target={target}
            underline="hover"
            color="inherit"
            className={classes.footerLinkInline}
          >
            {navLinkText}
          </Link>
        ))}
      </Stack>
    );
  };

  const renderBotLinks = () => {
    // Get last 3 routes and filter out "Help", also change "Privacy Policy" to "Privacy"
    const bottomLinks = FOOTER_ROUTES.slice(-3)
      .filter((route) => route.navLinkText !== 'Help')
      .map((route) => ({
        ...route,
        navLinkText:
          route.navLinkText === 'Privacy Policy' ? 'Privacy' : route.navLinkText,
      }));

    return (
      <Grid2
        container
        spacing={2}
        className={classes.botLinksContainer}
        sx={{
          alignItems: { xs: 'center', md: 'flex-end' },
          justifyContent: { xs: 'center', md: 'flex-end' },
        }}
      >
        {bottomLinks.map(({ navLinkText, to, external, target }, index) => (
          <Grid2 key={index}>
            <Link
              href={to}
              external={external}
              target={target}
              underline="hover"
              color="inherit"
              className={classes.footerLink2}
            >
              {navLinkText}
            </Link>
          </Grid2>
        ))}
      </Grid2>
    );
  };

  const footerContent = (
    <Paper
      component="footer"
      elevation={1}
      className={classes.root}
      data-component="Footer"
    >
      <Stack className={classes.footerStack} useFlexGap>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          className={classes.footerContentTop}
          useFlexGap
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems="center"
            className={classes.logoAndLinksContainer}
            useFlexGap
          >
            <Box className={classes.logoWrapper}>
              <Image
                fill
                className={classes.logoImage}
                src="https://elevator-media.imgix.net/elevator-header-logo.png?ixlib=js-2.3.1&s=38f13353a3d61a22b222363a9032f2ce"
                alt="Elevator Logo"
                priority
                sizes="(max-width: 899px) 90px, 113px"
              />
            </Box>
            {renderTopLinks()}
          </Stack>
          {renderSocialMedia()}
        </Stack>

        <Divider className={classes.divider} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent={{ xs: 'center', md: 'space-between' }}
          alignItems={{ xs: 'center', md: 'flex-end' }}
          spacing={2}
          className={classes.footerBottom}
        >
          <Copyright />
          {renderBotLinks()}
        </Stack>
      </Stack>
    </Paper>
  );

  return <ThemeProvider theme={darkTheme}>{footerContent}</ThemeProvider>;
};

export default Footer;
